import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { USERAXIOS } from "../../api";
import { Currency } from "../../types/resume";
import { useMemo } from "react";

interface CurrenciesResponse {
  data: Currency[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useCurrenciesInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["currencies", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<CurrenciesResponse> => {
      const response = await USERAXIOS.get<CurrenciesResponse>(
        `/currencies?title=${searchTerm}`,
        {
          params: {
            page: pageParam,
            per_page: pageSize,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

// Hook for autocomplete usage
export const useCurrenciesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useCurrenciesInfinite(pageSize, searchTerm);

  // Flatten all pages into a single array
  const currencies = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: currencies,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
